import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { SignIn, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatErr = (detail) => {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function Login() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  // Fetch logo via public branding endpoint (no auth required)
  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/branding`)
      .then((r) => setLogoUrl(r.data.logo_url || ""))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden"
      data-testid="login-page"
    >
      {/* Decorative diagonal accent */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rotate-12 pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-primary/5 -rotate-12 pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo size="lg" logoUrl={logoUrl} />
        </div>

        <div className="bg-white border border-border p-8">
          <div className="mb-6">
            <div className="overline text-primary mb-2">Secure Portal</div>
            <h1 className="font-display text-3xl font-black tracking-tighter">
              Sign in
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Access your HEO IT Engineering invoice portal.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="overline">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="rounded-sm mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="overline">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-sm pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password visibility"
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm"
              data-testid="login-submit-btn"
            >
              <SignIn size={16} weight="bold" className="mr-2" />
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center text-xs text-muted-foreground">
          Contact the HEO administrator if you need an account.
        </div>
      </div>
    </div>
  );
}
